import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface SearchableSelectProps<T> {
  /** Items to render. Empty array shows the empty state. */
  items: T[];
  /** Currently selected key, or null/undefined when nothing is selected. */
  value: string | null | undefined;
  /** Called with the selected key, or null when cleared / "none" picked. */
  onValueChange: (value: string | null) => void;
  /** Stable unique key (typically the row id). */
  getKey: (item: T) => string;
  /** Primary display label shown in the trigger and the list row. */
  getLabel: (item: T) => string;
  /**
   * Optional secondary line shown muted under the label (e.g. national id,
   * phone, district). Not used for matching unless `getSearchString` is set.
   */
  getSubtitle?: (item: T) => string | null | undefined;
  /**
   * Override what the search query is matched against. Default: `getLabel`
   * concatenated with `getSubtitle` (when set). Use this to add extra
   * haystack fields (Arabic + English names, phone, id, etc.).
   */
  getSearchString?: (item: T) => string;
  /**
   * Optional trailing chip/badge for each row (and the selected trigger) —
   * e.g. an individual-vs-organization tag. Rendered at the end of the row.
   */
  getBadge?: (item: T) => React.ReactNode;
  /** Trigger placeholder when nothing is selected. */
  placeholder?: string;
  /** Search input placeholder. */
  searchPlaceholder?: string;
  /** Shown when no item matches the query. */
  emptyText?: string;
  /**
   * If set, renders a built-in "no selection" row with this label that
   * triggers `onValueChange(null)`.
   */
  noneLabel?: string;
  /** Extra trigger className (e.g. for `flex-1`, error border). */
  triggerClassName?: string;
  /** data-testid on the trigger button (E2E hook). */
  triggerTestId?: string;
  /** Disable the trigger. */
  disabled?: boolean;
  /** Show error styling on the trigger. */
  hasError?: boolean;
  /**
   * Slot rendered at the bottom of the list (e.g. an "+ Add new …" button).
   * Always visible regardless of search results.
   */
  footerSlot?: React.ReactNode;
}

/**
 * Generic typeahead picker — Popover + cmdk Command. Built once, used for
 * every owner / tenant / property / unit / employee dropdown across the app
 * so search behaviour is identical everywhere.
 *
 * Selection model: stores a single string key. Pass `noneLabel` to allow
 * an explicit "none" selection that calls back with `null`.
 */
export function SearchableSelect<T>({
  items,
  value,
  onValueChange,
  getKey,
  getLabel,
  getSubtitle,
  getSearchString,
  getBadge,
  placeholder,
  searchPlaceholder,
  emptyText,
  noneLabel,
  triggerClassName,
  triggerTestId,
  disabled,
  hasError,
  footerSlot,
}: SearchableSelectProps<T>) {
  const [open, setOpen] = React.useState(false);

  const selected = React.useMemo(
    () => items.find((item) => getKey(item) === value) ?? null,
    [items, value, getKey],
  );

  const triggerLabel = selected
    ? getLabel(selected)
    : value === null && noneLabel
      ? noneLabel
      : (placeholder ?? "");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={triggerTestId}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !selected && "text-muted-foreground",
            hasError && "border-destructive",
            triggerClassName,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-start">{triggerLabel}</span>
            {selected && getBadge ? (
              <span className="shrink-0">{getBadge(selected)}</span>
            ) : null}
          </span>
          <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        // When this popover renders inside a Radix Dialog (most of the
        // pickers across the app), the dialog applies a scroll-lock that
        // intercepts wheel/touch events at the document level. The popover
        // is portalled to body but its events still bubble up — without
        // these handlers the inner CommandList list never scrolls.
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command
          filter={(itemValue, search) => {
            // itemValue is the `value` we set on each CommandItem (the key).
            // We resolve it back to the underlying row and run the match
            // ourselves so callers control the haystack via getSearchString.
            const item = items.find((i) => getKey(i) === itemValue);
            if (!item) {
              // The "none" row — match if its label matches the query.
              if (itemValue === "__none__" && noneLabel) {
                return noneLabel.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
              }
              return 0;
            }
            const haystack = getSearchString
              ? getSearchString(item)
              : `${getLabel(item)} ${getSubtitle?.(item) ?? ""}`;
            return haystack.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="overscroll-contain">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {noneLabel && (
                <CommandItem
                  value="__none__"
                  onSelect={() => {
                    onValueChange(null);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "me-2 h-4 w-4",
                      value === null || value === undefined ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="text-muted-foreground">{noneLabel}</span>
                </CommandItem>
              )}
              {items.map((item) => {
                const key = getKey(item);
                const subtitle = getSubtitle?.(item);
                return (
                  <CommandItem
                    key={key}
                    value={key}
                    onSelect={() => {
                      onValueChange(key);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "me-2 h-4 w-4 shrink-0",
                        value === key ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate">{getLabel(item)}</span>
                        {subtitle ? (
                          <span className="text-muted-foreground truncate text-xs">{subtitle}</span>
                        ) : null}
                      </div>
                      {getBadge ? <span className="shrink-0">{getBadge(item)}</span> : null}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {footerSlot ? <div className="border-t p-1">{footerSlot}</div> : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
